import React from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { Loader, Toggle, Emoji } from '@loyalup/ui';

import { ReactComponent as EditIcon } from 'icons/edit.svg';
import { ReactComponent as DotsIcon } from 'icons/dots.svg';
import { ReactComponent as BacksideIcon } from 'icons/backside.svg';

import s from './Card.module.css';

const AZTEC = require('assets/images/barcodes/AZTEC.png');
const CODE128 = require('assets/images/barcodes/CODE128.png');
const PDF417 = require('assets/images/barcodes/PDF417.png');
const QR = require('assets/images/barcodes/QR.png');

const barcodes = { AZTEC, CODE128, PDF417, QR };
const emojis = {
  PHONE: <Emoji symbol="☎️" label="telephone" />,
  LINK: <Emoji symbol="🌍" label="earth" />,
  LOCATION: <Emoji symbol="📍" label="pushpin" />,
  TEXT: null,
};

const getBarcodePreview = barcodeName => (barcodes[barcodeName] ? barcodes[barcodeName] : null);
const getIcon = type => (emojis[type] ? emojis[type] : null);

const FrontCardField = ({
  className,
  foregroundColor,
  labelColor,
  label,
  value,
  align,
  binding,
  setEditFrontField,
  size,
  selected,
  maxTextLength,
}) => {
  let valueText = '';
  if (value) valueText = value;
  if (!value && binding) valueText = `@${binding}`;

  return (
    <div
      onMouseDown={e => {
        // prevent hide edit form
        e.stopPropagation();
        setEditFrontField({ value, label, binding });
      }}
      className={cn(s.cardItem, className, { [s.selected]: selected })}
    >
      <span className={cn(s.label, s[align])} style={{ color: labelColor }}>
        {label}
      </span>
      <span style={{ color: foregroundColor }} className={cn(s.value, s[align], { [s.bigValue]: size === 'big' })}>
        {Boolean(maxTextLength) && !binding && valueText.length > maxTextLength
          ? valueText.slice(0, maxTextLength) + '...'
          : valueText}
      </span>
    </div>
  );
};

const CardFront = React.memo(
  ({ cardData, hintPreview, onSetBackgroundImage, onSetLogoImage, editFrontField, setEditFrontField }) => {
    const [t] = useTranslation();

    const renderHint = className => (
      <span className={cn(s.hint, className)}>
        <BacksideIcon className={s.backside} width="13" height="11" />
        {t('Подробности на обороте карты. Нажмите на')}
        <DotsIcon className={s.dots} width="10" height="10" />
        {t('в углу карты.')}
      </span>
    );

    return (
      <div style={{ backgroundColor: cardData?.backgroundColor }} className={s.root}>
        <div className={s.header}>
          <div className={s.logo}>
            <img
              src={cardData?.logoImage?.url}
              alt=""
              onClick={onSetLogoImage}
              className={cn(s.logoImage, { [s.touchable]: !cardData?.logoLoading })}
            />
            {cardData?.logoLoading ? (
              <Loader className={s.imageLoader} />
            ) : (
              <div className={s.changeImage}>
                <EditIcon />
              </div>
            )}
          </div>
          <span className={s.spacer} />
          {cardData?.headerFields?.map(fieldData => (
            <FrontCardField
              foregroundColor={cardData?.foregroundColor}
              labelColor={cardData?.labelColor}
              key={fieldData.index}
              maxTextLength={17}
              {...fieldData}
              setEditFrontField={data => setEditFrontField({ type: 'headerFields', index: fieldData?.index, ...data })}
              selected={editFrontField?.type === 'headerFields' && editFrontField?.index === fieldData.index}
              size="big"
            />
          ))}
        </div>
        <div className={s.stripImageContainer}>
          {cardData?.bannerEnabled && hintPreview && (
            <div className={s.banner}>
              <span className={s.push}>{t('Здесь будет выводиться последнее полученное push-уведомление')}</span>
              {renderHint()}
            </div>
          )}
          {!hintPreview && cardData?.hintEnabled && renderHint(s.hintNoBanner)}
          <img
            alt=""
            src={cardData?.stripImage?.url}
            className={cn(s.stripImage, { [s.touchable]: !cardData?.imageLoading })}
            onClick={onSetBackgroundImage}
          />
          {cardData?.imageLoading ? (
            <Loader className={s.imageLoader} />
          ) : (
            <div className={s.changeImage}>
              <EditIcon />
            </div>
          )}
        </div>
        <div className={s.secondaryFields}>
          {cardData?.secondaryFields?.map(fieldData => (
            <FrontCardField
              foregroundColor={cardData?.foregroundColor}
              labelColor={cardData?.labelColor}
              key={fieldData.index}
              {...fieldData}
              className={s.cardItemBottom}
              maxTextLength={25}
              selected={editFrontField?.type === 'secondaryFields' && editFrontField?.index === fieldData.index}
              setEditFrontField={data =>
                setEditFrontField({ type: 'secondaryFields', index: fieldData.index, ...data })
              }
            />
          ))}
        </div>
        {cardData?.barcodeEnabled && (
          <div className={s.barcodeContainer}>
            <div
              style={{ backgroundImage: `url(${getBarcodePreview(cardData?.barcodeType)})` }}
              className={cn(s.barcode, s[cardData.barcodeType])}
            />
            <span className={s.centered}>{cardData?.barcodeAltMessage}</span>
          </div>
        )}
      </div>
    );
  },
);

const InfoFieldRow = ({ type, text, value }) => (
  <div className={cn(s.infoFieldRow, s.message)}>
    <div className={s.infoFieldIcon}>{getIcon(type)}</div>
    <span className={cn({ [s.link]: type === 'LINK' })}>{text}</span>
    {type === 'PHONE' && value && <span className={cn(s.link, s.leftSpace)}>{value}</span>}
  </div>
);

const CardBack = ({ cardData }) => {
  const [t] = useTranslation();

  return (
    <div className={cn(s.root, s.back)}>
      {/* TODO preview */}
      <div className={s.rowContainer}>
        <Toggle label={t('Автообновление')} onColor={'#4ad862'} checked={true} onChange={() => {}} className={s.row} />
        <Toggle
          label={t('Допуск уведомлений')}
          onColor={'#4ad862'}
          checked={true}
          onChange={() => {}}
          className={s.row}
        />
      </div>
      <div className={s.rowContainer}>
        <div className={s.row}>
          <span className={s.sharePass}>{t('Поделиться картой')}</span>
        </div>
      </div>

      <div className={s.rowContainer}>
        <div className={s.row}>
          <span className={s.removePass}>{t('Удалить карту')}</span>
        </div>
      </div>

      <div className={s.rowContainer}>
        <div className={s.row}>
          <div className={s.fields}>
            <span className={s.title}>{t('Последнее сообщение')}</span>
            <span className={s.message}>{cardData?.lastMessage}</span>
          </div>
        </div>

        {Object.keys(cardData?.infoFields || {}).length && (
          <div className={s.row}>
            <div className={s.fields}>
              <span className={s.title}>{t('Возможности')}</span>
              {Object.keys(cardData.infoFields).map(id => (
                <InfoFieldRow
                  id={id}
                  type={cardData.infoFields[id].type}
                  text={cardData.infoFields[id].label}
                  value={cardData.infoFields[id].value}
                />
              ))}
            </div>
          </div>
        )}

        {cardData?.feedbackEnabled && (
          <div className={s.row}>
            <div className={s.fields}>
              <span className={s.title}>{t('Обратная связь')}</span>
              <span className={s.link}>{cardData.feedbackLabel}</span>
            </div>
          </div>
        )}

        {cardData?.backFields &&
          Object.keys(cardData.backFields).map(id => (
            <div className={s.row}>
              <div className={s.fields}>
                <span className={s.title}>{cardData.backFields[id].label}</span>
                <span className={cn(s.message, s.withWrap)}>{cardData.backFields[id].value}</span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export { CardFront, CardBack };
